import { SortDirection } from '@/enums/sort';
import { sortGroupTableValuesByDirection } from '@/components/group-tables/use-group-table-view-model-core';

export type GroupTableMetrics = {
    reviewCount: number;
    answerCount: number;
    answerPercent: number | null;
    aiAnalysisCount: number;
    averageRating: number | null;
    nps: number | null;
};

type GroupTableMetricSource = {
    reviewCount?: unknown;
    answerCount?: unknown;
    answerPercent?: unknown;
    aiAnalysisCount?: unknown;
    averageRating?: unknown;
    nps?: unknown;
};

export const normalizeGroupTableFilterText = (value: string | null | undefined): string =>
    (value || '').trim().toLowerCase();

export const toGroupTableMetrics = (source: GroupTableMetricSource): GroupTableMetrics => {
    const reviewCount = typeof source.reviewCount === 'number' ? source.reviewCount : 0;
    const answerCount = typeof source.answerCount === 'number' ? source.answerCount : 0;
    const answerPercent =
        reviewCount > 0
            ? typeof source.answerPercent === 'number'
                ? source.answerPercent
                : (answerCount / reviewCount) * 100
            : null;

    return {
        reviewCount,
        answerCount,
        answerPercent,
        aiAnalysisCount: typeof source.aiAnalysisCount === 'number' ? source.aiAnalysisCount : 0,
        averageRating: typeof source.averageRating === 'number' ? source.averageRating : null,
        nps: typeof source.nps === 'number' ? source.nps : null,
    };
};

export type GroupTableCommonSortKey = keyof GroupTableMetrics;

const GROUP_TABLE_COMMON_SORT_KEYS = new Set<GroupTableCommonSortKey>([
    'reviewCount',
    'answerCount',
    'answerPercent',
    'aiAnalysisCount',
    'averageRating',
    'nps',
]);

export const isGroupTableCommonSortKey = (value: unknown): value is GroupTableCommonSortKey =>
    typeof value === 'string' && GROUP_TABLE_COMMON_SORT_KEYS.has(value as GroupTableCommonSortKey);

export const sortGroupTableByCommonMetric = <TRecord extends GroupTableMetrics>(
    leftRecord: TRecord,
    rightRecord: TRecord,
    sortKey: GroupTableCommonSortKey,
    direction: SortDirection,
): number => sortGroupTableValuesByDirection(leftRecord[sortKey], rightRecord[sortKey], direction);

export type GroupTableCustomSortComparator<TRecord> = (
    leftRecord: TRecord,
    rightRecord: TRecord,
    direction: SortDirection,
) => number;

export type GroupTableCustomSortComparators<TRecord> = Partial<
    Record<keyof TRecord, GroupTableCustomSortComparator<TRecord>>
>;

export const sortGroupTableWithComparators = <TRecord extends GroupTableMetrics>({
    items,
    sortKey,
    direction,
    comparators,
}: {
    items: TRecord[];
    sortKey: keyof TRecord;
    direction: SortDirection;
    comparators: GroupTableCustomSortComparators<TRecord>;
}): TRecord[] => {
    const sortedItems = [...items];
    sortedItems.sort((leftRecord, rightRecord) => {
        const compare = comparators[sortKey];
        if (compare) {
            return compare(leftRecord, rightRecord, direction);
        }

        if (isGroupTableCommonSortKey(sortKey)) {
            return sortGroupTableByCommonMetric(leftRecord, rightRecord, sortKey, direction);
        }

        return 0;
    });

    return sortedItems;
};
